/**
 * Price Range Slider Component
 * A custom dual-range slider for Shopify price filtering
 * Compatible with facets.js and Shopify's native filtering system
 */

class PriceSlider extends HTMLElement {
  constructor() {
    super();
    // Select inputs by Shopify's filter parameter names (gte = greater than or equal, lte = less than or equal)
    this.minInput = this.querySelector('input[name*=".gte"]') || this.querySelector('input[id*="-GTE"]');
    this.maxInput = this.querySelector('input[name*=".lte"]') || this.querySelector('input[id*="-LTE"]');
    this.slider = null;
    this.isDragging = false;
    this.dragTimeout = null;
    
    if (!this.minInput || !this.maxInput) {
      return;
    }
    
    this.init();
  }

  init() {
    // Get min and max values from data attributes
    this.rangeMin = 0;
    const maxFromAttr = this.maxInput.getAttribute('data-max');
    // Parse number: remove thousands separators, convert comma decimal to dot
    this.rangeMax = maxFromAttr ? this.parsePrice(maxFromAttr) : 1000;
    
    // Set step size (€10 increments)
    this.step = 10;
    
    // Get current values from inputs
    const currentMin = this.parsePrice(this.minInput.value) || this.rangeMin;
    const currentMax = this.parsePrice(this.maxInput.value) || this.rangeMax;
    
    // Create slider container
    this.createSliderHTML();
    
    // Initialize slider state
    // Don't snap max if it's at the range max (keep the actual max price)
    this.values = {
      min: this.snapToStep(currentMin),
      max: currentMax >= this.rangeMax ? this.rangeMax : this.snapToStep(currentMax)
    };
    
    // Update initial position
    this.updateSliderVisuals();
    
    // Bind events
    this.bindEvents();
    
    // Hide text inputs when JS is enabled (they'll still work as fallback)
    this.classList.add('price-slider--initialized');
  }

  parsePrice(value) {
    if (!value) return 0;
    // Detect format: if comma comes after dot, it's European (1.234,56)
    // If dot comes after comma, it's US (1,234.56)
    const str = value.toString().trim();
    const lastComma = str.lastIndexOf(',');
    const lastDot = str.lastIndexOf('.');
    
    if (lastComma > lastDot) {
      // European format: 1.234,56 or just 234,56
      // Remove dots (thousands), replace comma with dot (decimal)
      return parseFloat(str.replace(/\./g, '').replace(',', '.'));
    } else {
      // US format: 1,234.56 or just 234.56
      // Remove commas (thousands), keep dot (decimal)
      return parseFloat(str.replace(/,/g, ''));
    }
  }

  snapToStep(value) {
    // Round to nearest step (€10) and ensure 2 decimal precision
    return parseFloat((Math.round(value / this.step) * this.step).toFixed(2));
  }

  createSliderHTML() {
    const sliderContainer = document.createElement('div');
    sliderContainer.className = 'price-slider__container';
    sliderContainer.innerHTML = `
      <div class="price-slider__track-wrapper">
        <div class="price-slider__track">
          <div class="price-slider__range"></div>
        </div>
        <div class="price-slider__thumbs">
          <input type="range" class="price-slider__thumb price-slider__thumb--min visually-hidden" data-thumb="min" id="price-slider-min" name="price-slider-min" min="${this.rangeMin}" max="${this.rangeMax}" step="${this.step}" aria-label="Minimum price" />
          <div class="price-slider__thumb-visual price-slider__thumb--min" data-thumb="min" role="button" tabindex="0" aria-label="Minimum price"></div>
          <input type="range" class="price-slider__thumb price-slider__thumb--max visually-hidden" data-thumb="max" id="price-slider-max" name="price-slider-max" min="${this.rangeMin}" max="${this.rangeMax}" step="${this.step}" aria-label="Maximum price" />
          <div class="price-slider__thumb-visual price-slider__thumb--max" data-thumb="max" role="button" tabindex="0" aria-label="Maximum price"></div>
        </div>
      </div>
      <div class="price-slider__values">
        <span class="price-slider__value price-slider__value--min"></span>
        <span class="price-slider__value price-slider__value--max"></span>
      </div>
    `;
    
    // Insert slider before the first input field
    this.minInput.parentElement.parentElement.insertBefore(sliderContainer, this.minInput.parentElement);
    
    // Cache slider elements
    this.slider = sliderContainer;
    this.track = this.slider.querySelector('.price-slider__track');
    this.range = this.slider.querySelector('.price-slider__range');
    this.thumbMin = this.slider.querySelector('.price-slider__thumb-visual.price-slider__thumb--min');
    this.thumbMax = this.slider.querySelector('.price-slider__thumb-visual.price-slider__thumb--max');
    this.valueMin = this.slider.querySelector('.price-slider__value--min');
    this.valueMax = this.slider.querySelector('.price-slider__value--max');
  }

  bindEvents() {
    // Mouse events
    this.thumbMin.addEventListener('mousedown', (e) => this.startDrag(e, 'min'));
    this.thumbMax.addEventListener('mousedown', (e) => this.startDrag(e, 'max'));
    
    // Touch events
    this.thumbMin.addEventListener('touchstart', (e) => this.startDrag(e, 'min'), { passive: false });
    this.thumbMax.addEventListener('touchstart', (e) => this.startDrag(e, 'max'), { passive: false });
    
    // Keyboard events
    this.thumbMin.addEventListener('keydown', (e) => this.handleKeyboard(e, 'min'));
    this.thumbMax.addEventListener('keydown', (e) => this.handleKeyboard(e, 'max'));
    
    // Track click to jump
    this.track.addEventListener('click', (e) => this.handleTrackClick(e));
    
    // Listen to manual input changes
    this.minInput.addEventListener('change', () => this.syncFromInputs());
    this.maxInput.addEventListener('change', () => this.syncFromInputs());
    
    // Listen for filter updates (when clear all is clicked)
    window.addEventListener('popstate', () => this.handleFilterReset());
    
    // Use MutationObserver to detect when inputs are cleared by AJAX updates
    this.observeInputChanges();
  }

  observeInputChanges() {
    // Watch for when inputs value attributes change
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          setTimeout(() => this.handleFilterReset(), 100);
          return;
        }
      }
    });
    
    // Observe the actual input elements
    if (this.minInput) {
      observer.observe(this.minInput, { attributes: true, attributeFilter: ['value'] });
    }
    if (this.maxInput) {
      observer.observe(this.maxInput, { attributes: true, attributeFilter: ['value'] });
    }
    
    // Also watch for when the entire price-range element gets replaced
    const checkInterval = setInterval(() => {
      const currentMin = this.parsePrice(this.minInput.value) || this.rangeMin;
      const currentMax = this.parsePrice(this.maxInput.value) || this.rangeMax;
      
      // If values differ from slider state, update
      if (Math.abs(currentMin - this.values.min) > 0.01 || Math.abs(currentMax - this.values.max) > 0.01) {
        this.handleFilterReset();
      }
    }, 500);
    
    // Store interval ID for cleanup if needed
    this.checkInterval = checkInterval;
  }

  handleFilterReset() {
    // Re-read values from inputs (which will be reset by Shopify)
    const currentMin = this.parsePrice(this.minInput.value) || this.rangeMin;
    const currentMax = this.parsePrice(this.maxInput.value) || this.rangeMax;
    
    this.values = {
      min: currentMin >= this.rangeMax ? this.rangeMin : this.snapToStep(currentMin),
      max: currentMax >= this.rangeMax ? this.rangeMax : this.snapToStep(currentMax)
    };
    
    this.updateSliderVisuals();
  }

  startDrag(event, thumb) {
    event.preventDefault();
    this.isDragging = true;
    this.currentThumb = thumb;
    
    const moveHandler = (e) => this.onDrag(e);
    const endHandler = () => this.endDrag(moveHandler, endHandler);
    
    document.addEventListener('mousemove', moveHandler);
    document.addEventListener('mouseup', endHandler);
    document.addEventListener('touchmove', moveHandler, { passive: false });
    document.addEventListener('touchend', endHandler);
  }

  onDrag(event) {
    if (!this.isDragging) return;
    
    event.preventDefault();
    const clientX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX;
    const rect = this.track.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const value = this.rangeMin + (this.rangeMax - this.rangeMin) * percent;
    
    // Snap to step size (€10 increments), but allow exact max
    let snappedValue;
    if (this.currentThumb === 'max' && percent > 0.98) {
      // If dragging max thumb near the end, snap to actual max
      snappedValue = this.rangeMax;
    } else {
      snappedValue = this.snapToStep(value);
    }
    
    if (this.currentThumb === 'min') {
      this.values.min = Math.min(snappedValue, this.values.max - this.step);
    } else {
      this.values.max = Math.max(snappedValue, this.values.min + this.step);
      // Ensure max doesn't exceed range max
      this.values.max = Math.min(this.values.max, this.rangeMax);
    }
    
    this.updateSliderVisuals();
    this.updateInputs();
    
    // Debounce the form submission
    clearTimeout(this.dragTimeout);
    this.dragTimeout = setTimeout(() => {
      this.triggerFilterUpdate();
    }, 800);
  }

  endDrag(moveHandler, endHandler) {
    this.isDragging = false;
    document.removeEventListener('mousemove', moveHandler);
    document.removeEventListener('mouseup', endHandler);
    document.removeEventListener('touchmove', moveHandler);
    document.removeEventListener('touchend', endHandler);
  }

  handleKeyboard(event, thumb) {
    let updated = false;
    
    switch(event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        event.preventDefault();
        if (thumb === 'min') {
          this.values.min = Math.max(this.rangeMin, this.values.min - this.step);
        } else {
          this.values.max = Math.max(this.values.min + this.step, this.values.max - this.step);
        }
        updated = true;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        event.preventDefault();
        if (thumb === 'min') {
          this.values.min = Math.min(this.values.max - this.step, this.values.min + this.step);
        } else {
          this.values.max = Math.min(this.rangeMax, this.values.max + this.step);
        }
        updated = true;
        break;
    }
    
    if (updated) {
      this.updateSliderVisuals();
      this.updateInputs();
      
      clearTimeout(this.dragTimeout);
      this.dragTimeout = setTimeout(() => {
        this.triggerFilterUpdate();
      }, 800);
    }
  }

  handleTrackClick(event) {
    if (event.target !== this.track && event.target !== this.range) return;
    
    const rect = this.track.getBoundingClientRect();
    const percent = (event.clientX - rect.left) / rect.width;
    const value = this.rangeMin + (this.rangeMax - this.rangeMin) * percent;
    const snappedValue = this.snapToStep(value);
    
    // Determine which thumb is closer
    const distToMin = Math.abs(snappedValue - this.values.min);
    const distToMax = Math.abs(snappedValue - this.values.max);
    
    if (distToMin < distToMax) {
      this.values.min = Math.max(this.rangeMin, Math.min(snappedValue, this.values.max - this.step));
    } else {
      this.values.max = Math.min(this.rangeMax, Math.max(snappedValue, this.values.min + this.step));
    }
    
    this.updateSliderVisuals();
    this.updateInputs();
    this.triggerFilterUpdate();
  }

  updateSliderVisuals() {
    const minPercent = ((this.values.min - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100;
    const maxPercent = ((this.values.max - this.rangeMin) / (this.rangeMax - this.rangeMin)) * 100;
    
    this.thumbMin.style.left = `${minPercent}%`;
    this.thumbMax.style.left = `${maxPercent}%`;
    this.range.style.left = `${minPercent}%`;
    this.range.style.width = `${maxPercent - minPercent}%`;
    
    // Format currency values - detect format from the original input
    const currencySymbol = this.querySelector('.field-currency')?.textContent?.trim() || '€';
    const originalValue = this.maxInput.placeholder || this.maxInput.value;
    const useCommaDecimal = originalValue && originalValue.includes(',');
    
    // Format to 2 decimal points
    const minValue = this.values.min.toFixed(2);
    const maxValue = this.values.max.toFixed(2);
    
    let minFormatted, maxFormatted;
    if (useCommaDecimal) {
      // European format: 1.234,56
      minFormatted = this.formatEuropean(minValue);
      maxFormatted = this.formatEuropean(maxValue);
    } else {
      // US format: 1,234.56
      minFormatted = this.formatUS(minValue);
      maxFormatted = this.formatUS(maxValue);
    }
    
    this.valueMin.textContent = `${currencySymbol}${minFormatted}`;
    this.valueMax.textContent = `${currencySymbol}${maxFormatted}`;
  }

  formatEuropean(value) {
    // Split into integer and decimal parts
    const [integer, decimal] = value.split('.');
    // Add thousands separator (dot) and use comma for decimal
    const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${formattedInteger},${decimal}`;
  }

  formatUS(value) {
    // Split into integer and decimal parts
    const [integer, decimal] = value.split('.');
    // Add thousands separator (comma) and use dot for decimal
    const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${formattedInteger}.${decimal}`;
  }

  updateInputs() {
    // Format to 2 decimal points for Shopify
    this.minInput.value = this.values.min.toFixed(2);
    this.maxInput.value = this.values.max.toFixed(2);
  }

  syncFromInputs() {
    const minValue = this.parsePrice(this.minInput.value) || this.rangeMin;
    const maxValue = this.parsePrice(this.maxInput.value) || this.rangeMax;
    
    // Snap to step size
    this.values.min = this.snapToStep(Math.max(this.rangeMin, Math.min(minValue, maxValue - this.step)));
    this.values.max = this.snapToStep(Math.min(this.rangeMax, Math.max(maxValue, minValue + this.step)));
    
    this.updateSliderVisuals();
  }

  triggerFilterUpdate() {
    // Trigger the change event on the inputs to activate facets.js
    const changeEvent = new Event('input', { bubbles: true });
    this.minInput.dispatchEvent(changeEvent);
  }
}

// Register custom element
customElements.define('price-slider', PriceSlider);

